import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/lib/cn';
import { inCampaign, useCampaign } from '@lib/campaigns';
import { CampaignScopeNote } from '@components/CampaignScopeNote';
import { EventDrawer } from '@components/EventDrawer';
import { openEvent } from '@lib/open-mode';
import { ThreadEventList } from '@components/ThreadTable';
import { handledIds, pressSentence } from '@components/ReplyActions';
import { CareGrid } from '@components/grid/CareGrid';
import { GridOperatorContext, useGridView, type GridColumn, type GridView } from '@components/grid/useGridView';
import { multiSort, sliceRows, type SlicerDef } from '@lib/grid';
import { useEventQueue } from '@lib/useEventQueue';
import { clientDef, useClient, useFeatures, useFeaturesSettled } from '@lib/clients';
import { isReceiveOnly, RECEIVE_ONLY_WORDING } from '@lib/receive-only';
import { careMarks, getEvent, memberComms, replayIdOf, replyActions, saveNotes, type ReplyActionsResp } from '@lib/care-api';
import { fmtMemberId } from '@lib/member-id';
import { portalMemberUrl } from '@lib/portal-link';
import { REPLY_OP_WORDING, inPresses, replyOfferLabel, type ReplyOp } from '@lib/reply-groups';
import { STAGED_WORDING, isApprovalPress } from '@lib/staged-presses';
import {
  BLOCKED_ROW_STYLE, CLOSED_STATUSES, LANES, LANE_WORDING, NOTES_SAVE_DELAY_MS, OPT_IN_WORDING, archivedByMember, fmtDateTimePt, fmtTsPt,
  inForceFromReply, indicationLabel, initialsOfEmail, isBlockedRow, isNonTextOptOut, issueMarks, issuesText, kitCell, kitSliceValue, laneBulkTakes, laneCounts, laneFromSearch,
  laneGreenIds, laneReferIds, laneRows, laneTat, laneTickable, markLines, markWords, markedByWords, marksByMember, matchesOptInQuery,
  memberIdsOf, memberKey, notesMark, otherCommsOf, readTypedInitials, rowMark, scopeNote, threadsByMember,
  typedInitialsOk, unnamedCohortCount, writeTypedInitials, MAX_TYPED_INITIALS,
  type LaneKind, type NotesState, type OptInScope, type RowMark,
} from '@lib/opt-in-lane';
import {
  SORT_FOLDERS, SORT_KINDS, SORT_WORDING, matchesSortQuery, searchWithFolder,
  sortFromSearch, sortRows, sortSignals, whyText, whyValues,
  type FolderKind, type SortKind,
} from '@lib/sort-folders';
import {
  CAMPAIGN_META, CHANNEL_META, STATUS_META, campaignLabelOf, fmtMinutes, fmtTs, statusLabel,
  type CareMarkRow, type CareMarksResp, type EventRow, type MemberCommRow,
} from '@lib/care-model';

/**
 * OptInQueue - ROW 400 (approval 193) grown into his 01A - NEW OPT IN (row 400 rev2, his blueprint
 * "Care Adjustments - Opt Ins" of 19 Sep 2026, approval 199): every list row the server put in the
 * Opt In group (approval 188, the member's LAST answer per campaign is yes), one row per member and
 * campaign, on row 356's shared grid, with his columns in his order: a tick box, Member ID, Name (a
 * link to the member's page on the main portal when the deployment names a shape), Opt In date/time
 * (Pacific), Indication (actual = a bare 1, implied = any other yes; the server's one rule), Text (the
 * member's words), Issues (a later STOP or 2, the do-not-text list, the opt-out record, the plan's
 * do-not-solicit mark), Other comms (expandable: the member's open entries from the general queue's own
 * thread grouping, and the archived history from member_care.db read ONCE for the members on screen),
 * TAT (time since the member answered, ticking), Notes (autosaved through the drawer's notes route),
 * then Cohort, Kit (the published cohort-to-kit map), Campaign, Offer and Status. Every column sorts.
 * A search box at the top searches THIS list only.
 *
 * Above the columns, his two pills: GREEN Approve (today's Opt In press, one code path with the
 * Inbound queue's button: the approved line with the kit code, "Approved by MH" on the archived entry,
 * the /1.1 envelope to the Portal) and YELLOW Send to Main Dock (the under-review line, a new entry in
 * the main queue naming who sent it, the entry archived). The header box ticks every row on screen
 * Select all may take: never a Read first row and never a blocked row (the 188 rule), though both stay
 * tickable one by one because Send to Main Dock takes a blocked row.
 *
 * The rows are the SAME list read the Inbound queue makes (useEventQueue with the thread grouping, one
 * storage walk). Opening a row opens the same EventDrawer. DARK: exists only when features.replyButtons
 * is on (MEMBERCARE_REPLY_BUTTONS), the switch the reply groups ride.
 *
 * ROW 419 (William, 19 Sep 2026; approval 211): the page carries THREE LANES on one grid, Opt In (today's,
 * byte for byte), Opt Out and STOP (lib/opt-in-lane.ts LANES: the rules, the words). The lane rides the
 * URL (`?lane=optout`) so a reload keeps it; since ROW 485 (20 Sep 2026) it is picked in the LEFT MENU
 * (01A New Opt In, 01B New Opt Out, 01C New STOP), not by buttons on the page. On the two new lanes
 * the GREEN pill is the EXISTING op (opt-out, stop) worded as Care's approval (his R. 87: the block is already in force from the reply),
 * the YELLOW pill stays wired to the refer op, a row already carrying its mark shows the mark instead
 * of a tick box, and a "Marked" toggle lists the archived marks read-only (GET /api/care/care-marks).
 * The Other comms panel prints the members' marks beside the archived history on every tab.
 */

const nowrap = { whiteSpace: 'nowrap' } as const;

/** Row 356: the two slicers he asked for. The Kit slicer groups by the kit the map names; a row it names none for is "No kit named". */
const OPT_IN_SLICERS: SlicerDef<EventRow>[] = [
  { key: 'cohort', label: 'Cohort', value: (r) => r.replyCohort, blankLabel: OPT_IN_WORDING.noCohort },
  { key: 'kit', label: 'Kit', value: kitSliceValue, blankLabel: OPT_IN_WORDING.noKitNamed },
];

function NameCell({ r, link }: { r: EventRow; link: string }): JSX.Element {
  const name = r.memberName || (r.memberId ? 'member' : '');
  if (!name) return <span className="muted">{OPT_IN_WORDING.unmatched}</span>;
  // A link ONLY when the deployment names a shape and every placeholder in it has a value; else text.
  const url = link ? portalMemberUrl(link, { campaign: r.campaign, member: r.memberId }) : null;
  if (!url) return <span>{name}</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title="Open the member's page on the main portal (new tab)"
      // The link is its own action: it must not also open the drawer.
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {name}
    </a>
  );
}

function KitCellView({ r }: { r: EventRow }): JSX.Element {
  const k = kitCell(r);
  if (k.none) return <span className="muted">{OPT_IN_WORDING.noCohort}</span>;
  if (k.pending) return <span className="muted" title={OPT_IN_WORDING.columnsTip.kit}>{k.text}</span>;
  return <span>{k.text}</span>;
}

/**
 * THE NOTES CELL, autosaved. The list row carries only `hasNotes` (the projection is PHI-light and the
 * note bodies stay off it), so a cell with notes on file LOADS them on the first click through the
 * drawer's own read (getEvent, audited as a view) before it lets her type; a cell with none is ready
 * at once. Every keystroke arms one save (NOTES_SAVE_DELAY_MS) through the drawer's notes route,
 * a notes-only body, which checks the claim and never takes the entry. The mark beside the box says
 * Unsaved, Saving, Saved, or why not. A closed entry (All scope) is read only here.
 */
export function NotesCell({ r, onSaved }: { r: EventRow; onSaved: () => void }): JSX.Element {
  const terminal = CLOSED_STATUSES.has(r.status);
  const [text, setText] = useState('');
  const [loaded, setLoaded] = useState(!r.hasNotes);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<NotesState>('idle');
  const [why, setWhy] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef(text);
  latest.current = text;

  const load = async () => {
    if (loaded || loading) return;
    setLoading(true);
    try {
      const ev = await getEvent(r.eventId);
      setText(ev.notes ?? '');
      setLoaded(true);
    } catch (err) {
      setState('failed');
      setWhy(`could not load the notes (${(err as Error).message})`);
    } finally {
      setLoading(false);
    }
  };

  const save = useCallback(async (value: string) => {
    setState('saving');
    const res = await saveNotes(r.eventId, { notes: value });
    // A later keystroke already re-armed: its save says the last word.
    if (latest.current !== value) return;
    if (res.ok) {
      setState('saved');
      onSaved();
    } else {
      setState('failed');
      setWhy(String((res.data as { error?: string }).error ?? `HTTP ${res.status}`));
    }
  }, [r.eventId, onSaved]);

  const onChange = (value: string) => {
    setText(value);
    setState('dirty');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { void save(value); }, NOTES_SAVE_DELAY_MS);
  };
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  if (terminal) {
    return <span className="muted micro" title={OPT_IN_WORDING.notes.readOnly}>{r.hasNotes ? '✎' : '-'}</span>;
  }
  return (
    <div style={{ display: 'grid', gap: 2 }} data-testid={`optin-notes-${r.eventId}`}>
      <textarea
        className="textarea-input optin-notes"
        rows={1}
        value={text}
        placeholder={loaded ? OPT_IN_WORDING.notes.placeholder : loading ? OPT_IN_WORDING.notes.loading : OPT_IN_WORDING.notes.loadFirst}
        aria-label={`Notes for ${r.memberName || r.memberId || r.eventId}`}
        readOnly={!loaded}
        onFocus={() => { void load(); }}
        onClick={(e) => { e.stopPropagation(); void load(); }}
        onKeyDown={(e) => e.stopPropagation()}
        onChange={(e) => onChange(e.target.value)}
      />
      {state !== 'idle' && (
        <span className={cn('micro', state === 'failed' ? 'optin-notes-failed' : 'muted')} role="status" data-testid={`optin-notes-mark-${r.eventId}`}>
          {notesMark(state, why)}
        </span>
      )}
    </div>
  );
}

interface ColumnDeps {
  /** Row 419: which tab the grid is on; Opt In renders exactly as before. */
  lane: LaneKind;
  /** Row 419: the mark a row already carries, or null (always null on Opt In). */
  markOf: (r: EventRow) => RowMark | null;
  link: string;
  nowMs: number;
  checked: ReadonlySet<string>;
  onToggle: (id: string) => void;
  /** The header tick box's state and action. */
  allOn: boolean;
  onToggleAll: () => void;
  bulkCount: number;
  comms: (r: EventRow) => { open: EventRow[]; archived: MemberCommRow[] };
  expanded: ReadonlySet<string>;
  onToggleExpand: (id: string) => void;
  onNotesSaved: () => void;
}

/** His columns in his order. The tick box's header is the Select all box. */
function optInColumns(d: ColumnDeps): GridColumn<EventRow>[] {
  const tip = OPT_IN_WORDING.columnsTip;
  return [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          aria-label={OPT_IN_WORDING.press.selectAll(d.bulkCount)}
          title={tip.select}
          checked={d.allOn}
          disabled={d.bulkCount === 0 && !d.allOn}
          onChange={() => d.onToggleAll()}
          onClick={(e) => e.stopPropagation()}
          data-testid="optin-select-all"
        />
      ),
      menuLabel: 'Tick box', locked: true, stopRowClick: true, title: tip.select,
      thStyle: { width: 28 }, tdStyle: { width: 28 },
      render: (r) => {
        const mark = d.markOf(r);
        // Row 419: a row that already carries its mark shows the mark and its time instead of a tick box.
        if (mark) {
          return (
            <span
              className="pill pill-accent"
              style={{ whiteSpace: 'nowrap' }}
              title={`${markWords(mark)}${mark.byName ? ` (${mark.byName})` : ''}${mark.via === 'member' ? '; recorded on another entry of this member (member_care.db, up to ten minutes behind)' : ''}`}
              data-testid={`optin-mark-${r.eventId}`}
            >
              {markWords(mark)}
            </span>
          );
        }
        // C0876ADB point 6: a voicemail or an email asking out takes no press here; it is opened and recorded on the case.
        if (d.lane === 'optout' && isNonTextOptOut(r)) {
          return (
            <span className="pill pill-warning" style={{ whiteSpace: 'nowrap' }} title={LANE_WORDING.nonTextHelp(r.channel)} data-testid={`optin-nontext-${r.eventId}`}>
              {LANE_WORDING.nonText(r.channel)}
            </span>
          );
        }
        const can = laneTickable(r, d.lane, mark);
        const blocked = isBlockedRow(r);
        const inForce = inForceFromReply(r, d.lane);
        return (
          <input
            type="checkbox"
            aria-label={`Select ${r.memberName || r.memberId || r.eventId}`}
            checked={can && d.checked.has(r.eventId)}
            disabled={!can}
            title={!can ? 'This row takes neither press' : blocked ? 'Blocked for Approve (see Issues); Send to Main Dock still takes it' : inForce ? `${LANE_WORDING.inForce}: ${LANE_WORDING.inForceHelp[d.lane as 'optout' | 'stop']}` : undefined}
            onChange={() => d.onToggle(r.eventId)}
            onKeyDown={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      key: 'id', label: 'Member ID', title: tip.id, tdStyle: nowrap, sortValue: (r) => r.memberId,
      render: (r) => (r.memberId ? fmtMemberId(r.memberId) : <span className="muted">{OPT_IN_WORDING.unmatched}</span>),
    },
    { key: 'name', label: 'Name', title: tip.name, tdStyle: nowrap, sortValue: (r) => r.memberName || r.memberId, render: (r) => <NameCell r={r} link={d.link} /> },
    {
      key: 'optin', label: LANES[d.lane].dateLabel, title: tip.optin, tdStyle: nowrap, firstDir: 'desc',
      sortValue: (r) => Date.parse(r.receivedTs) || null, render: (r) => fmtTsPt(r.receivedTs),
    },
    {
      key: 'indication', label: 'Indication', title: tip.indication, tdStyle: nowrap, sortValue: indicationLabel,
      render: (r) => {
        const ind = r.replyIndication ?? '';
        if (!ind) return <span className="muted">-</span>;
        return (
          <span className={cn('pill', ind === 'actual' ? 'pill-success' : 'pill-warning')} title={OPT_IN_WORDING.indicationHelp[ind]} data-testid={`optin-indication-${r.eventId}`}>
            {indicationLabel(r)}
          </span>
        );
      },
    },
    {
      key: 'text', label: 'Text', title: tip.text,
      tdStyle: { maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
      sortValue: (r) => r.preview, render: (r) => <span title={r.preview}>{r.preview}</span>,
    },
    {
      key: 'issues', label: 'Issues', title: tip.issues, sortValue: issuesText,
      render: (r) => {
        const marks = issueMarks(r);
        if (!marks.length) return <span className="muted">{OPT_IN_WORDING.issues.none}</span>;
        return (
          <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {marks.map((m) => (
              <span key={m.key} className={`pill ${m.pill}`.trim()} title={m.title} data-testid={`optin-issue-${m.key}`}>
                {m.text}
              </span>
            ))}
          </span>
        );
      },
    },
    {
      key: 'comms', label: 'Other comms', title: tip.comms, tdStyle: nowrap, stopRowClick: true,
      sortValue: (r) => { const c = d.comms(r); return c.open.length + c.archived.length; },
      render: (r) => {
        const c = d.comms(r);
        const n = c.open.length + c.archived.length;
        if (!n) return <span className="muted">{OPT_IN_WORDING.comms.none}</span>;
        const open = d.expanded.has(r.eventId);
        return (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            aria-expanded={open}
            title={open ? OPT_IN_WORDING.comms.collapse : OPT_IN_WORDING.comms.expand}
            onClick={(e) => { e.stopPropagation(); d.onToggleExpand(r.eventId); }}
            onKeyDown={(e) => e.stopPropagation()}
            data-testid={`optin-comms-${r.eventId}`}
          >
            {open ? '▾' : '▸'} {OPT_IN_WORDING.comms.count(c.open.length, c.archived.length)}
          </button>
        );
      },
    },
    {
      key: 'tat', label: 'TAT', title: tip.tat, tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => laneTat(r, d.nowMs),
      render: (r) => {
        const mins = laneTat(r, d.nowMs);
        const open = r.tatMinutes == null;
        return mins === null
          ? <span className="muted">-</span>
          : <span className={open ? 'muted' : undefined} title={open ? 'since the member answered, ticking' : 'frozen: sent or approved'}>{fmtMinutes(mins)}</span>;
      },
    },
    {
      key: 'notes', label: 'Notes', title: tip.notes, stopRowClick: true, tdStyle: { minWidth: 180 },
      sortValue: (r) => (r.hasNotes ? 1 : 0),
      render: (r) => <NotesCell r={r} onSaved={d.onNotesSaved} />,
    },
    {
      key: 'cohort', label: 'Cohort', title: tip.cohort, tdStyle: nowrap, sortValue: (r) => r.replyCohort ?? '',
      render: (r) => r.replyCohort || <span className="muted">{OPT_IN_WORDING.noCohort}</span>,
    },
    { key: 'kit', label: 'Kit', title: tip.kit, tdStyle: nowrap, sortValue: (r) => kitCell(r).text, render: (r) => <KitCellView r={r} /> },
    {
      key: 'campaign', label: 'Campaign', title: tip.campaign, tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => campaignLabelOf(r),
      render: (r) => (r.campaign ? <span className={`pill ${CAMPAIGN_META[r.campaign]?.pill ?? ''}`.trim()}>{campaignLabelOf(r)}</span> : <span className="muted">{campaignLabelOf(r)}</span>),
    },
    { key: 'offer', label: 'Offer', title: tip.offer, tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => replyOfferLabel(r.replyOffer), render: (r) => replyOfferLabel(r.replyOffer) },
    {
      key: 'status', label: 'Status', title: tip.status, tdStyle: nowrap, sortValue: (r) => statusLabel(r.status),
      render: (r) => {
        const st = STATUS_META[r.status] ?? { label: r.status, pill: '' };
        return <span className={`pill ${st.pill}`.trim()}>{st.label}</span>;
      },
    },
  ];
}

function Frame({ lane, children }: { lane: LaneKind; children: ReactNode }): JSX.Element {
  return (
    <div className="page-pad">
      <header>
        <div className="section-eyebrow">{OPT_IN_WORDING.eyebrow}</div>
        <h1 className="section-title">{LANES[lane].title} <em>queue</em></h1>
        <div className="section-rule" />
        {/* His word, 16 Oct 2026: the description line under the title is removed ("Remove this."). */}
      </header>
      {children}
    </div>
  );
}

/**
 * ROW 419: the lane lives in the URL (`?lane=optout`), read off window.location so a reload keeps it and
 * the page needs no router context (its tests render it bare, as they always did).
 * ROW 485 (William, 20 Sep 2026): the three small tab buttons that used to sit here MOVED to the left
 * menu as 01A New Opt In, 01B New Opt Out, 01C New STOP (AppShell), so nothing on the page sets the
 * lane any more; routed, the page is handed the lane by OptInRoute below and this read is the bare
 * fallback (a direct link, a reload, the tests).
 */
function useLane(): { lane: LaneKind; sort: SortKind | null; pick: (f: FolderKind) => void } {
  const [search, setSearch] = useState<string>(() => (typeof window === 'undefined' ? '' : window.location.search));
  useEffect(() => {
    const onPop = () => setSearch(window.location.search);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  // William's auto sort (2 Oct 2026): the strip's bare pick (no router): the address is set so a reload
  // keeps the folder, and the page follows. Routed, OptInRoute hands the router's own navigate instead.
  const pick = useCallback((f: FolderKind) => {
    const next = searchWithFolder(window.location.search, f);
    window.history.pushState(window.history.state, '', `${window.location.pathname}${next}`);
    setSearch(next);
  }, []);
  return { lane: laneFromSearch(search), sort: sortFromSearch(search), pick };
}

interface ArchivedState {
  byMember: Map<string, MemberCommRow[]>;
  /** Row 419: the members' marks (Care's and the platform's) by trimmed member id, from the same read. */
  marks: Map<string, CareMarkRow[]>;
  builtAt: string;
  available: boolean;
  loading: boolean;
  error: string;
}
const NO_ARCHIVE: ArchivedState = { byMember: new Map(), marks: new Map(), builtAt: '', available: false, loading: false, error: '' };

/** The rows as the grid shows them: its slicers, then its sort, over the searched lane. ONE implementation of the row order (lib/grid.ts). */
function visibleRowsOf(view: GridView<EventRow>, rows: EventRow[]): EventRow[] {
  const sliced = sliceRows(rows, view.slicerDefs, view.slicers).rows;
  if (view.sort.length === 0) return sliced;
  const byKey = new Map(view.columns.map((c) => [c.key, c]));
  return multiSort(sliced, view.sort, (r, k) => byKey.get(k)?.sortValue?.(r));
}

const noop = () => undefined;

export function OptInQueue({ lane: routed, folder: routedSort }: { lane?: LaneKind; folder?: SortKind | null } = {}): JSX.Element {
  const fromUrl = useLane();
  const lane = routed ?? fromUrl.lane;
  // William's auto sort (2 Oct 2026): a sorted folder rides the same query key as the lanes.
  const sort = routed !== undefined ? (routedSort ?? null) : fromUrl.sort;
  if (sort) {
    return (
      <SortFrame kind={sort}>
        <SortBody key={sort} kind={sort} />
      </SortFrame>
    );
  }
  // Row 419: the body is keyed on the lane so its grid view, ticks and reads start fresh on every lane
  // (one body, three lanes, never three copies of it). Row 485: the lane is picked in the left menu.
  return (
    <Frame lane={lane}>
      <LaneBody key={lane} lane={lane} />
    </Frame>
  );
}

/**
 * Row 485: the menu lines 01A / 01B / 01C are ONE route whose query names the lane; since 5 Oct 2026
 * (his "Remove this block not needed", the office's option A) 01D to 01G name the four sorted folders on
 * the same key. The router's own location is handed to the page, so a menu move between two of them
 * (same path, new query) changes the list on screen. Only this wrapper touches the router; OptInQueue
 * still renders bare.
 */
export function OptInRoute(): JSX.Element {
  const { search } = useLocation();
  return <OptInQueue lane={laneFromSearch(search)} folder={sortFromSearch(search)} />;
}

function LaneBody({ lane }: { lane: LaneKind }): JSX.Element {
  const features = useFeatures();
  const settled = useFeaturesSettled();
  const enabled = settled && features.replyButtons;
  const client = useClient();
  /** His word 3 Oct 2026: a receive-only plan takes Opt Out and STOP only (lib/receive-only). */
  const receiveOnly = isReceiveOnly(client);
  const topCampaign = useCampaign();
  const def = LANES[lane];
  const [scope, setScope] = useState<OptInScope>('open');
  const [marked, setMarked] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  // The open-cases switch (William, 2 Oct 2026): the side panel below, or the case in its own window.
  const openCase = (id: string) => openEvent(id, setSelected);
  const [q, setQ] = useState('');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [banner, setBanner] = useState('');
  const [busy, setBusy] = useState<ReplyOp | ''>('');
  // K4 (Care's 1:03 PM point 5): a press whose platform or master's-door answer was UNKNOWN is sent again
  // on the SAME request id, so the retry replays what the platform or the door holds and writes nothing twice.
  const retry = useRef<{ op: ReplyOp; id: string } | null>(null);
  // Row 521: the operator's own initials, read once from this browser and remembered as they type.
  const [initials, setInitials] = useState<string>(() => readTypedInitials());
  const operatorEmail = useContext(GridOperatorContext);
  // Tick the TAT once a minute, as the queue does.
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  // The Inbound queue's own read WITH its thread grouping (the same cache key as its thread view):
  // one list, no second walk, and the member threads for the "other comms" column. Paused while the
  // switch is off or unknown, so a dark deployment reads nothing for this screen.
  const { data, reload } = useEventQueue({
    direction: 'inbound',
    notSource: 'out-calling',
    excludeFaxFailures: true,
    groupBy: 'thread',
    paused: !enabled,
  });

  // Row 354: the header's CAMPAIGN chip scopes this list too; a row naming no campaign always passes.
  // What it sets aside is counted over THIS lane's rows, not the whole queue's.
  const all = data?.items ?? [];
  const scoped = useMemo(() => all.filter((r) => inCampaign(r.campaign, topCampaign)), [all, topCampaign]);
  const counts = useMemo(() => laneCounts(scoped, lane), [scoped, lane]);
  const laneList = useMemo(() => laneRows(scoped, lane, scope), [scoped, lane, scope]);
  // His top-of-page search: this list only, in the browser.
  const items = useMemo(() => (q.trim() ? laneList.filter((r) => matchesOptInQuery(r, q)) : laneList), [laneList, q]);
  const setAside = laneRows(all, lane, scope).length - laneList.length;

  // The other comms: the general queue's thread grouping for the OPEN entries, and the archived
  // history from member_care.db, read ONCE for the members of the lane on screen (the id set is the
  // key, so a poll that changes nothing reads nothing again).
  const threads = useMemo(() => threadsByMember(data?.threads), [data?.threads]);
  const eventsById = useMemo(() => new Map(all.map((r) => [r.eventId, r])), [all]);
  const membersKey = useMemo(() => memberIdsOf(laneList).join(','), [laneList]);
  const [archive, setArchive] = useState<ArchivedState>(NO_ARCHIVE);
  useEffect(() => {
    if (!enabled || !membersKey) {
      setArchive(NO_ARCHIVE);
      return;
    }
    let live = true;
    setArchive((prev) => ({ ...prev, loading: true }));
    void memberComms(membersKey.split(','))
      .then((resp) => {
        if (!live) return;
        setArchive({ byMember: archivedByMember(resp.rows), marks: marksByMember(resp.marks), builtAt: resp.builtAt, available: resp.available, loading: false, error: resp.error ?? '' });
      })
      .catch((err: Error) => {
        if (!live) return;
        setArchive({ byMember: new Map(), marks: new Map(), builtAt: '', available: false, loading: false, error: err.message });
      });
    return () => { live = false; };
  }, [enabled, membersKey]);
  const comms = useCallback((r: EventRow) => otherCommsOf(r, threads, eventsById, archive.byMember), [threads, eventsById, archive.byMember]);
  // Row 419: the mark a row already carries (its own action, or a Care mark on the member from another entry).
  const markOf = useCallback((r: EventRow) => rowMark(r, lane, archive.marks), [lane, archive.marks]);

  const toggleCheck = useCallback((id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const toggleExpand = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);
  const onNotesSaved = useCallback(() => { void reload(); }, [reload]);

  // The grid's view is built on the columns' KEYS and flags (useGridView reads nothing else of them),
  // so a first list with a quiet header box gives the sort and the slicers, the rows ON SCREEN follow
  // from those, and the live columns (the header box's state, the counts) are handed to the grid.
  const baseColumns = useMemo(() => optInColumns({
    lane, markOf, link: features.portalMemberLink, nowMs, checked, onToggle: toggleCheck, allOn: false, onToggleAll: noop, bulkCount: 0,
    comms, expanded, onToggleExpand: toggleExpand, onNotesSaved,
  }), [lane, markOf, features.portalMemberLink, nowMs, checked, toggleCheck, comms, expanded, toggleExpand, onNotesSaved]);
  const view = useGridView(def.viewKey, baseColumns, { defaultSort: [{ k: 'optin', asc: false }], slicers: OPT_IN_SLICERS });
  // Keyed on what decides the order (the view object itself is rebuilt every render).
  const sortKey = JSON.stringify(view.sort);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const visibleRows = useMemo(() => visibleRowsOf(view, items), [sortKey, view.slicers, view.slicerDefs, view.columns, items]);
  // The rows the header box and Select all take (188: never Read first, never blocked; row 419: never a
  // marked row); the rest tick one by one.
  const bulkRows = useMemo(() => visibleRows.filter((r) => laneTickable(r, lane, markOf(r)) && laneBulkTakes(r, lane, markOf(r))), [visibleRows, lane, markOf]);
  const tickableCount = useMemo(() => visibleRows.filter((r) => laneTickable(r, lane, markOf(r))).length, [visibleRows, lane, markOf]);
  const allOn = bulkRows.length > 0 && bulkRows.every((r) => checked.has(r.eventId));
  const toggleAll = useCallback(() => {
    setChecked((prev) => (bulkRows.length > 0 && bulkRows.every((r) => prev.has(r.eventId))
      ? new Set<string>()
      : new Set([...prev, ...bulkRows.map((r) => r.eventId)])));
  }, [bulkRows]);
  // A ticked row a filter, a search or a press then hides is UNTICKED: a press must never reach a
  // row she cannot see.
  useEffect(() => {
    setChecked((prev) => {
      if (prev.size === 0) return prev;
      const shown = new Set(visibleRows.filter((r) => laneTickable(r, lane, markOf(r))).map((r) => r.eventId));
      const kept = [...prev].filter((id) => shown.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [visibleRows, lane, markOf]);
  const columns = useMemo(() => optInColumns({
    lane, markOf, link: features.portalMemberLink, nowMs, checked, onToggle: toggleCheck, allOn, onToggleAll: toggleAll, bulkCount: bulkRows.length,
    comms, expanded, onToggleExpand: toggleExpand, onNotesSaved,
  }), [lane, markOf, features.portalMemberLink, nowMs, checked, toggleCheck, allOn, toggleAll, bulkRows.length, comms, expanded, toggleExpand, onNotesSaved]);
  const liveView = useMemo<GridView<EventRow>>(
    () => ({ ...view, columns, visible: columns.filter((c) => !view.hidden.has(c.key)) }),
    [view, columns],
  );

  if (!settled) {
    return <div className="muted" style={{ padding: '20px 0' }}>{OPT_IN_WORDING.loading}</div>;
  }
  if (!features.replyButtons) {
    return <div className="note warn" role="status" data-testid="optin-not-enabled">{OPT_IN_WORDING.notEnabled}</div>;
  }

  const map = data?.cohortKit;
  const unnamed = unnamedCohortCount(items);
  const plan = clientDef(client).displayName;
  const approveN = laneGreenIds(visibleRows, checked, lane, markOf).length;
  const referN = laneReferIds(visibleRows, checked, lane, markOf).length;
  const greenOp = def.greenOp;

  const press = async (op: 'opt-in' | 'opt-out' | 'stop' | 'refer') => {
    const ids = op === 'refer' ? laneReferIds(visibleRows, checked, lane, markOf) : laneGreenIds(visibleRows, checked, lane, markOf);
    if (!ids.length) return;
    const label = op === 'refer' ? OPT_IN_WORDING.press.refer : def.green;
    const ask = op === 'refer' ? OPT_IN_WORDING.press.confirmRefer(ids.length) : def.confirmGreen(ids.length);
    // Row 521: a bad value never reaches the server, which would refuse the whole press by name.
    if (!typedInitialsOk(initials)) { setBanner(OPT_IN_WORDING.press.initialsBad); return; }
    // WAVE 3 ITEM 4 (William, 2 Oct 2026: "all items go to approval then take effect"): the lane's GREEN
    // press (Approve, Approve opt out, Approve STOP) IS the approval, so it says so on the wire and
    // acts at once; Send to Main Dock never does, so with presses staged it is queued for approval and
    // its confirm box says so. Only while the deployment stages presses: off, every press and every
    // confirm box here is byte for byte what it was.
    const approval = features.stagedPresses && op === greenOp && isApprovalPress(op);
    const queued = features.stagedPresses && !approval;
    if (!window.confirm(`${ask}\n\n${REPLY_OP_WORDING[op].help}${queued ? `\n\n${STAGED_WORDING.confirmQueued}` : ''}`)) return;
    let reason: string | undefined;
    if (op === 'refer') {
      const typed = window.prompt(OPT_IN_WORDING.press.referReasonPrompt, '');
      if (typed === null) return; // Cancel on the prompt cancels the press
      reason = typed.trim() || undefined;
    }
    setBusy(op);
    const answers: ReplyActionsResp[] = [];
    const failures: string[] = [];
    try {
      const parts = inPresses(ids);
      for (let i = 0; i < parts.length; i += 1) {
        const replay = parts.length === 1 && retry.current?.op === op ? retry.current.id : undefined;
        const r = approval
          ? await replyActions(op, parts[i]!, replay, reason, initials || undefined, true)
          : await replyActions(op, parts[i]!, replay, reason, initials || undefined);
        if (!r.ok) {
          failures.push(String((r.data as { error?: string }).error ?? `HTTP ${r.status}`));
          // Row 496 (20 Sep 2026): the first failed part ends the run. Firing the remaining parts into
          // the same fault made 36 more errors on his screen and nothing else.
          if (i + 1 < parts.length) failures.push(OPT_IN_WORDING.press.stoppedAfter(i + 1, parts.length));
          break;
        }
        answers.push(r.data as ReplyActionsResp);
        const again = replayIdOf(r.data as ReplyActionsResp);
        retry.current = again ? { op, id: again } : null;
      }
      // The entries the press took: done, or (wave 3 item 4) queued for approval; both are unticked.
      const done = handledIds(answers);
      const sentence = answers.length ? pressSentence(label, answers) : '';
      setBanner([sentence, failures.length ? `${label} could not be sent: ${failures.join('; ')}.` : ''].filter(Boolean).join(' '));
      setChecked((prev) => new Set([...prev].filter((id) => !done.includes(id))));
      await reload();
    } finally {
      setBusy('');
    }
  };

  // Row 419: the two new tabs carry the note in the operator's words and, when asked, the Marked view.
  if (marked && lane !== 'optin') {
    return (
      <>
        <div className="note" role="note" data-testid="optin-lane-note">{def.note(scopeNote(laneList, clientDef(client).programScopes))}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '10px 0' }} data-testid="optin-scope">
          <button type="button" className="chip selected" aria-pressed="true" title={LANE_WORDING.markedHelp} onClick={() => setMarked(false)} data-testid="optin-marked-toggle">
            {LANE_WORDING.marked}
          </button>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setMarked(false)}>{LANE_WORDING.markedOff}</button>
        </div>
        <MarkedView lane={lane} campaign={topCampaign === undefined || topCampaign === null ? null : String(topCampaign)} plan={plan} onOpen={openCase} />
        {selected && (
          <EventDrawer eventId={selected} hasAudioHint={false} onClose={() => setSelected(null)} onChanged={() => { void reload(); }} />
        )}
      </>
    );
  }

  return (
    <>
      {lane !== 'optin' && (
        <div className="note" role="note" data-testid="optin-lane-note" style={{ marginBottom: 10 }}>
          {def.note(scopeNote(laneList, clientDef(client).programScopes))}
        </div>
      )}
      {/* The map's state, SAID: a blank Kit cell must be told from a map that never loaded. */}
      {data && (!map || !map.loaded) && (
        <div className="note warn" role="status" data-testid="optin-map-notice" style={{ marginBottom: 14 }}>
          {OPT_IN_WORDING.mapNotLoaded(map?.error ?? null)}
        </div>
      )}
      {map?.loaded && (
        <div className="micro muted" role="status" data-testid="optin-map-loaded" style={{ marginBottom: 8 }}>
          {OPT_IN_WORDING.mapLoaded(map.entries)}{unnamed > 0 ? ` ${OPT_IN_WORDING.mapUnnamed(unnamed)}` : ''}
        </div>
      )}

      <CampaignScopeNote others={setAside} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }} data-testid="optin-scope">
        {(['open', 'all'] as const).map((k) => (
          <button
            key={k}
            type="button"
            className={cn('chip', scope === k && 'selected')}
            aria-pressed={scope === k}
            title={OPT_IN_WORDING.scopeHelp[k]}
            onClick={() => setScope(k)}
          >
            {OPT_IN_WORDING.scope[k]} ({counts[k]})
          </button>
        ))}
        <input
          className="text-input"
          type="search"
          aria-label={OPT_IN_WORDING.search.label}
          placeholder={OPT_IN_WORDING.search.placeholder}
          style={{ marginLeft: 'auto', minWidth: 320 }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="optin-search"
        />
        {q && (
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setQ('')}>
            {OPT_IN_WORDING.search.clear}
          </button>
        )}
        {lane !== 'optin' && (
          <button type="button" className="chip" aria-pressed="false" title={LANE_WORDING.markedHelp} onClick={() => setMarked(true)} data-testid="optin-marked-toggle">
            {LANE_WORDING.marked}
          </button>
        )}
      </div>

      {data?.doNotTextChecked === false && (
        <div className="note warn" style={{ marginBottom: 8 }} role="note" data-testid="dnt-unchecked">
          Do-not-text list not checked: the texting platform&apos;s list could not be read, so no row is marked for it.
          Approve still reads it on every press and refuses any entry it cannot check.
        </div>
      )}

      {/* His two pills, above the columns. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }} data-testid="optin-press-bar">
        <button type="button" className="btn btn-ghost btn-sm" onClick={toggleAll} disabled={!bulkRows.length && !checked.size} data-testid="optin-select-all-button">
          {allOn ? OPT_IN_WORDING.press.clear : OPT_IN_WORDING.press.selectAll(bulkRows.length)}
        </button>
        {bulkRows.length < tickableCount && (
          <span className="micro muted" data-testid="optin-read-first-count">
            {OPT_IN_WORDING.press.readFirst(tickableCount - bulkRows.length)}
          </span>
        )}
        {/* Row 521: the operator's own initials. Typed once, remembered in this browser, stamped on
            every entry a press closes. The signed-in account rides underneath on every press. */}
        <label
          className="micro muted"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          title={OPT_IN_WORDING.press.initialsHelp}
        >
          {OPT_IN_WORDING.press.initialsLabel}
          <input
            type="text"
            value={initials}
            maxLength={MAX_TYPED_INITIALS}
            size={4}
            placeholder={OPT_IN_WORDING.press.initialsPlaceholder}
            aria-label={OPT_IN_WORDING.press.initialsLabel}
            aria-invalid={!typedInitialsOk(initials)}
            data-testid="optin-initials"
            style={{ width: '4.2em', textTransform: 'uppercase' }}
            onChange={(e) => {
              const v = e.target.value.toUpperCase();
              setInitials(v);
              if (typedInitialsOk(v)) writeTypedInitials(v);
            }}
          />
        </label>
        {!typedInitialsOk(initials) && (
          <span className="micro warn" role="alert" data-testid="optin-initials-bad">{OPT_IN_WORDING.press.initialsBad}</span>
        )}
        <button
          type="button"
          className="btn btn-sm btn-pill-approve"
          disabled={busy !== '' || approveN === 0 || (receiveOnly && greenOp === 'opt-in')}
          title={receiveOnly && greenOp === 'opt-in' ? RECEIVE_ONLY_WORDING.pressRefused : def.greenHelp}
          onClick={() => void press(greenOp)}
          data-testid="optin-approve"
        >
          {busy === greenOp ? 'Working...' : `${def.green} (${approveN})`}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-pill-refer"
          disabled={busy !== '' || referN === 0 || receiveOnly}
          title={receiveOnly ? RECEIVE_ONLY_WORDING.pressRefused : lane === 'optin' ? OPT_IN_WORDING.press.referHelp : `${OPT_IN_WORDING.press.referHelp} ${LANE_WORDING.referNotHere}`}
          onClick={() => void press('refer')}
          data-testid="optin-refer"
        >
          {busy === 'refer' ? 'Working...' : `${OPT_IN_WORDING.press.refer} (${referN})`}
        </button>
        {lane !== 'optin' && (
          <span className="micro muted" data-testid="optin-refer-note">{LANE_WORDING.referNotHere}</span>
        )}
      </div>

      {/* Row 521: said in words, so nobody has to guess what the press will stamp. */}
      <div className="micro muted" style={{ marginBottom: 10 }} data-testid="optin-initials-note">
        {typedInitialsOk(initials) && initials
          ? OPT_IN_WORDING.press.initialsTyped(initials, operatorEmail)
          : OPT_IN_WORDING.press.initialsAccount(initialsOfEmail(operatorEmail), operatorEmail)}
      </div>

      {banner && (
        <div className="note" style={{ marginBottom: 14 }} role="status" data-testid="optin-banner">
          {banner}
        </div>
      )}

      <CareGrid
        view={liveView}
        rows={items}
        rowKey={(r) => r.eventId}
        noun={OPT_IN_WORDING.noun}
        tableWrapClassName="card card-flush"
        // Until the first answer lands there is nothing to be empty OF: say loading, not "no replies".
        empty={data ? (q.trim() && laneList.length ? def.emptySearch(q.trim()) : def.empty(plan, scope)) : OPT_IN_WORDING.loading}
        testId="optin-grid"
        rowProps={(r) => {
          const blocked = isBlockedRow(r);
          const reason = blocked ? issueMarks(r).find((m) => m.key === 'later-refusal')?.text ?? '' : '';
          const isSelected = selected === r.eventId;
          return {
            tabIndex: 0,
            role: 'button',
            'data-testid': `optin-row-${r.eventId}`,
            'aria-label': `Open ${r.memberName || r.memberId || 'unmatched'}, ${statusLabel(r.status)}${blocked ? `, ${OPT_IN_WORDING.blocked}: ${reason}` : ''}`,
            title: blocked ? `${OPT_IN_WORDING.blocked}: ${reason}` : undefined,
            onClick: () => openCase(r.eventId),
            onKeyDown: (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openCase(r.eventId);
              }
            },
            style: {
              cursor: 'pointer',
              ...(blocked && !isSelected ? BLOCKED_ROW_STYLE : {}),
              ...(isSelected ? { background: 'var(--paper-2)' } : {}),
            },
          };
        }}
        after={(r) => {
          if (!expanded.has(r.eventId)) return null;
          const c = comms(r);
          return (
            <div style={{ padding: '6px 12px 10px 34px', background: 'var(--paper-2)' }} data-testid={`optin-comms-list-${r.eventId}`}>
              {c.open.length > 0 && (
                <>
                  <div className="micro muted" style={{ margin: '2px 8px' }}>{OPT_IN_WORDING.comms.openHead}</div>
                  <ThreadEventList events={c.open} selectedEventId={selected} onSelectEvent={openCase} />
                </>
              )}
              <div className="micro muted" style={{ margin: '6px 8px 2px' }} data-testid={`optin-comms-note-${r.eventId}`}>
                {archive.loading
                  ? OPT_IN_WORDING.comms.archivedLoading
                  : archive.available
                    ? OPT_IN_WORDING.comms.archivedHead(fmtTs(archive.builtAt))
                    : OPT_IN_WORDING.comms.archivedUnread}
              </div>
              {c.archived.length > 0 && (
                <ThreadEventList events={c.archived} selectedEventId={selected} onSelectEvent={openCase} />
              )}
              {/* Row 419: the member's marks beside the history, from the same read (member_care.db). */}
              {(archive.marks.get(memberKey(r.memberId)) ?? []).length > 0 && (
                <ul className="micro" style={{ margin: '6px 8px 0', paddingLeft: 16 }} data-testid={`optin-comms-marks-${r.eventId}`}>
                  {markLines(archive.marks.get(memberKey(r.memberId)) ?? []).map((line, i) => <li key={i}>{line}</li>)}
                </ul>
              )}
            </div>
          );
        }}
      />

      {selected && (
        <EventDrawer
          eventId={selected}
          hasAudioHint={false}
          onClose={() => setSelected(null)}
          onChanged={() => { void reload(); }}
        />
      )}
    </>
  );
}

/* ------------------------------------------ 2 Oct 2026: the four sorted folders ------------------------------------------ */

function SortFrame({ kind, children }: { kind: SortKind; children: ReactNode }): JSX.Element {
  return (
    <div className="page-pad">
      <header>
        <div className="section-eyebrow">{SORT_WORDING.eyebrow}</div>
        <h1 className="section-title">{SORT_FOLDERS[kind].title} <em>folder</em></h1>
        <div className="section-rule" />
        {/* His word, 16 Oct 2026: the description line under the title is removed, as on the lanes. */}
      </header>
      {children}
    </div>
  );
}

/** One slicer set per folder, built once (the grid keys on the definitions). */
const SORT_SLICERS: Readonly<Record<SortKind, SlicerDef<EventRow>[]>> = Object.fromEntries(
  SORT_KINDS.map((k) => [k, [
    { key: 'why', label: 'Why', value: (r: EventRow) => whyValues(r, k) },
    { key: 'channel', label: 'Channel', value: (r: EventRow) => CHANNEL_META[r.channel]?.label ?? r.channel },
  ] as SlicerDef<EventRow>[]]),
) as Record<SortKind, SlicerDef<EventRow>[]>;

/**
 * A sorted folder's columns: the lanes' own where they apply (Member ID, Name, the date, Text, Issues,
 * TAT, Campaign, Status), plus Channel and Why. NO tick box and NO press: sorting only.
 */
function sortColumns(d: { kind: SortKind; link: string; nowMs: number }): GridColumn<EventRow>[] {
  const tip = OPT_IN_WORDING.columnsTip;
  const own = SORT_WORDING.columnsTip;
  return [
    {
      key: 'id', label: 'Member ID', title: tip.id, tdStyle: nowrap, sortValue: (r) => r.memberId,
      render: (r) => (r.memberId ? fmtMemberId(r.memberId) : <span className="muted">{OPT_IN_WORDING.unmatched}</span>),
    },
    { key: 'name', label: 'Name', title: tip.name, tdStyle: nowrap, sortValue: (r) => r.memberName || r.memberId, render: (r) => <NameCell r={r} link={d.link} /> },
    {
      key: 'received', label: 'Received', title: own.received, tdStyle: nowrap, firstDir: 'desc',
      sortValue: (r) => Date.parse(r.receivedTs) || null, render: (r) => fmtTsPt(r.receivedTs),
    },
    {
      key: 'channel', label: 'Channel', title: own.channel, tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => CHANNEL_META[r.channel]?.label ?? r.channel,
      render: (r) => <span className={`pill ${CHANNEL_META[r.channel]?.pill ?? ''}`.trim()}>{CHANNEL_META[r.channel]?.label ?? r.channel}</span>,
    },
    {
      key: 'why', label: 'Why', title: own.why, sortValue: (r) => whyText(r, d.kind),
      render: (r) => (
        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {sortSignals(r, d.kind).map((w) => (
            <span key={`${w.signal}:${w.code}`} className="pill" title={w.title} data-testid={`sort-why-${w.signal}`}>{w.text}</span>
          ))}
        </span>
      ),
    },
    {
      key: 'text', label: 'Text', title: tip.text,
      tdStyle: { maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
      sortValue: (r) => r.preview, render: (r) => <span title={r.preview}>{r.preview}</span>,
    },
    {
      key: 'issues', label: 'Issues', title: tip.issues, sortValue: issuesText,
      render: (r) => {
        const marks = issueMarks(r);
        if (!marks.length) return <span className="muted">{OPT_IN_WORDING.issues.none}</span>;
        return (
          <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {marks.map((m) => <span key={m.key} className={`pill ${m.pill}`.trim()} title={m.title}>{m.text}</span>)}
          </span>
        );
      },
    },
    {
      key: 'tat', label: 'TAT', title: own.tat, tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => laneTat(r, d.nowMs),
      render: (r) => {
        const mins = laneTat(r, d.nowMs);
        return mins === null ? <span className="muted">-</span> : <span className={r.tatMinutes == null ? 'muted' : undefined}>{fmtMinutes(mins)}</span>;
      },
    },
    {
      key: 'campaign', label: 'Campaign', title: tip.campaign, tdClassName: 'small', tdStyle: nowrap, sortValue: (r) => campaignLabelOf(r),
      render: (r) => (r.campaign ? <span className={`pill ${CAMPAIGN_META[r.campaign]?.pill ?? ''}`.trim()}>{campaignLabelOf(r)}</span> : <span className="muted">{campaignLabelOf(r)}</span>),
    },
    {
      key: 'status', label: 'Status', title: own.status, tdStyle: nowrap, sortValue: (r) => statusLabel(r.status),
      render: (r) => {
        const st = STATUS_META[r.status] ?? { label: r.status, pill: '' };
        return <span className={`pill ${st.pill}`.trim()}>{st.label}</span>;
      },
    },
  ];
}

/**
 * ONE SORTED FOLDER (William, 2 Oct 2026): the open entries its rule selects (lib/sort-folders.ts), out of
 * the SAME list read the lanes and the Inbound queue make. Sorting only: no tick box, no press, nothing
 * sent; a row opens the case the way the lanes open one.
 */
function SortBody({ kind }: { kind: SortKind }): JSX.Element {
  const features = useFeatures();
  const settled = useFeaturesSettled();
  const enabled = settled && features.replyButtons;
  const client = useClient();
  const topCampaign = useCampaign();
  const def = SORT_FOLDERS[kind];
  const [selected, setSelected] = useState<string | null>(null);
  const openCase = (id: string) => openEvent(id, setSelected);
  const [q, setQ] = useState('');
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);
  // The lanes' own read (the same cache key): one list, no second walk.
  const { data, reload } = useEventQueue({
    direction: 'inbound',
    notSource: 'out-calling',
    excludeFaxFailures: true,
    groupBy: 'thread',
    paused: !enabled,
  });
  const all = data?.items ?? [];
  const scoped = useMemo(() => all.filter((r) => inCampaign(r.campaign, topCampaign)), [all, topCampaign]);
  const list = useMemo(() => sortRows(scoped, kind), [scoped, kind]);
  const items = useMemo(() => (q.trim() ? list.filter((r) => matchesSortQuery(r, kind, q)) : list), [list, kind, q]);
  const setAside = sortRows(all, kind).length - list.length;
  const columns = useMemo(() => sortColumns({ kind, link: features.portalMemberLink, nowMs }), [kind, features.portalMemberLink, nowMs]);
  const view = useGridView(def.viewKey, columns, { defaultSort: [{ k: 'received', asc: false }], slicers: SORT_SLICERS[kind] });

  if (!settled) {
    return <div className="muted" style={{ padding: '20px 0' }}>{SORT_WORDING.loading}</div>;
  }
  if (!features.replyButtons) {
    return <div className="note warn" role="status" data-testid="optin-not-enabled">{SORT_WORDING.notEnabled}</div>;
  }
  const plan = clientDef(client).displayName;
  const hasAudio = selected ? !!all.find((r) => r.eventId === selected)?.hasAudio : false;

  return (
    <>
      {/* The folder's ONE rule, said on the screen, then the order rule and what the list never does. */}
      <div className="note" role="note" data-testid="sort-folder-note" style={{ marginBottom: 6 }}>{def.note}</div>
      <div className="micro muted" data-testid="sort-folder-rule" style={{ marginBottom: 10 }}>
        {SORT_WORDING.orderRule} {SORT_WORDING.sortingOnly}
      </div>

      <CampaignScopeNote others={setAside} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }} data-testid="sort-scope">
        <span className="chip selected" title={SORT_WORDING.openHelp} data-testid="sort-open-count">{SORT_WORDING.openCount(list.length)}</span>
        <input
          className="text-input"
          type="search"
          aria-label={SORT_WORDING.search.label}
          placeholder={SORT_WORDING.search.placeholder}
          style={{ marginLeft: 'auto', minWidth: 320 }}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="sort-search"
        />
        {q && (
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => setQ('')}>
            {SORT_WORDING.search.clear}
          </button>
        )}
      </div>

      <CareGrid
        view={view}
        rows={items}
        rowKey={(r) => r.eventId}
        noun={SORT_WORDING.noun}
        tableWrapClassName="card card-flush"
        empty={data ? (q.trim() && list.length ? def.emptySearch(q.trim()) : def.empty(plan)) : SORT_WORDING.loading}
        testId="sort-grid"
        rowProps={(r) => ({
          tabIndex: 0,
          role: 'button',
          'data-testid': `sort-row-${r.eventId}`,
          'aria-label': `Open ${r.memberName || r.memberId || 'unmatched'}, ${statusLabel(r.status)}`,
          onClick: () => openCase(r.eventId),
          onKeyDown: (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openCase(r.eventId);
            }
          },
          style: { cursor: 'pointer', ...(selected === r.eventId ? { background: 'var(--paper-2)' } : {}) },
        })}
      />

      {selected && (
        <EventDrawer eventId={selected} hasAudioHint={hasAudio} onClose={() => setSelected(null)} onChanged={() => { void reload(); }} />
      )}
    </>
  );
}

/* ------------------------------------------ row 419: the Marked view ------------------------------------------ */

interface MarkedState {
  resp: CareMarksResp | null;
  loading: boolean;
  error: string;
}

/**
 * The ARCHIVED entries carrying this tab's mark (QA's item 4): Care's presses and the platform's own
 * reading of the reply, from the builder's care_marks table through GET /api/care/care-marks. Read
 * only: no tick box, no press, nothing here lifts a mark (a lift is recorded on the Exceptions review
 * screens). A row opens the entry in the drawer, which is read only for an archived entry.
 */
function MarkedView({ lane, campaign, plan, onOpen }: { lane: LaneKind; campaign: string | null; plan: string; onOpen: (id: string) => void }): JSX.Element {
  const def = LANES[lane];
  const [state, setState] = useState<MarkedState>({ resp: null, loading: true, error: '' });
  const scoped = campaign && campaign !== '*' && campaign !== 'all' ? campaign : null;
  useEffect(() => {
    let live = true;
    setState({ resp: null, loading: true, error: '' });
    void careMarks(def.markKind, lane === 'optout' ? scoped : null)
      .then((resp) => { if (live) setState({ resp, loading: false, error: '' }); })
      .catch((err: Error) => { if (live) setState({ resp: null, loading: false, error: err.message }); });
    return () => { live = false; };
  }, [def.markKind, lane, scoped]);
  const cols = LANE_WORDING.markedColumns;
  const resp = state.resp;
  return (
    <div data-testid="optin-marked-view">
      {state.loading && <div className="muted" style={{ padding: '12px 0' }}>{LANE_WORDING.markedLoading}</div>}
      {!state.loading && (state.error || (resp && !resp.available)) && (
        <div className="note warn" role="status" data-testid="optin-marked-unread">{LANE_WORDING.markedUnread}{state.error ? ` (${state.error})` : resp?.error ? ` (${resp.error})` : ''}</div>
      )}
      {!state.loading && resp?.available && resp.tableAbsent && (
        <div className="note warn" role="status" data-testid="optin-marked-absent">{LANE_WORDING.markedTableAbsent}</div>
      )}
      {!state.loading && resp?.available && !resp.tableAbsent && (
        <>
          <div className="micro muted" style={{ marginBottom: 8 }} data-testid="optin-marked-head">
            {LANE_WORDING.markedHead(fmtTs(resp.builtAt))}{resp.total > resp.count ? ` Showing the newest ${resp.count} of ${resp.total}.` : ''}
          </div>
          {resp.rows.length === 0 ? (
            <div className="muted" style={{ padding: '12px 0' }} data-testid="optin-marked-empty">{def.markedEmpty(plan)}</div>
          ) : (
            <div className="card card-flush">
              <table className="table" data-testid="optin-marked-table">
                <thead>
                  <tr>
                    <th>{cols.id}</th><th>{cols.name}</th><th>{cols.when}</th><th>{cols.by}</th><th>{cols.source}</th>
                    {lane === 'optout' && <th>{cols.campaign}</th>}
                    {lane === 'optout' && <th>{cols.scope}</th>}
                    <th>{cols.text}</th><th>{cols.status}</th><th>{cols.entry}</th>
                  </tr>
                </thead>
                <tbody>
                  {resp.rows.map((m) => (
                    <tr
                      key={`${m.eventId}:${m.kind}:${m.markedAt}`}
                      data-testid={`optin-marked-row-${m.eventId}`}
                      tabIndex={0}
                      role="button"
                      style={{ cursor: 'pointer' }}
                      onClick={() => onOpen(m.eventId)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen(m.eventId); } }}
                    >
                      <td style={nowrap}>{m.memberId ? fmtMemberId(m.memberId) : <span className="muted">{OPT_IN_WORDING.unmatched}</span>}</td>
                      <td style={nowrap}>{m.memberName || <span className="muted">{OPT_IN_WORDING.unmatched}</span>}</td>
                      <td style={nowrap}>{fmtDateTimePt(m.markedAt)}</td>
                      <td style={nowrap} title={m.markedByName || m.markedBy}>{markedByWords(m.markedBy, m.source)}</td>
                      <td style={nowrap}>{m.source === 'care' ? LANE_WORDING.sourceCare : LANE_WORDING.sourcePlatform}</td>
                      {lane === 'optout' && <td style={nowrap}>{m.campaign ? (CAMPAIGN_META[m.campaign]?.label ?? m.campaign) : <span className="muted">-</span>}</td>}
                      {lane === 'optout' && <td style={nowrap}>{m.program && m.year !== null ? `${m.program} ${m.year}` : <span className="muted">{LANE_WORDING.scopeCampaignOnly}</span>}</td>}
                      <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.sourceText}>{m.sourceText}</td>
                      <td style={nowrap}>{statusLabel(m.statusAtBuild as EventRow['status'])}</td>
                      <td className="small" style={nowrap}>{m.eventId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
